import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, Globe, Clock, Ticket, ArrowLeft, Mail, Navigation, ShieldCheck, Accessibility, Car } from "lucide-react";
import { api, fileUrl, thumbUrl } from "../lib/api";
import { ListingHeroImg } from "../lib/termImages";
import { EventCard } from "../components/Cards";
import { Reveal, SectionTitle, useSeo } from "../components/VIP";
import { ListingActions } from "../components/ListingActions";
import { Paged } from "../components/PagedList";
import { Sponsors } from "../components/Marketplace";
import { VideoGrid } from "../components/VideoGrid";
import { DealsSection, MerchGrid } from "../components/VenueExtras";
import { ReviewsSection } from "../components/Reviews";
import LoyaltyJoinCard from "../components/LoyaltyJoinCard";
import { NominateButton, AwardBadges, VoteCard } from "../components/AwardsWidgets";
import { WeatherPanel } from "../components/Weather";
import { PipeText } from "../lib/pipeText";

const MAP = (q) => `https://maps.google.com/maps?q=${encodeURIComponent(q)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;

const Info = ({ Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <Icon size={16} className="text-rose-400 mt-0.5 shrink-0" />
    <div><p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p><div className="text-sm text-zinc-200">{children}</div></div>
  </div>
);

function ExperienceCard({ x }) {
  const closed = ["Closed", "Temporarily Closed"].includes(x.status);
  return (
    <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden" data-testid={`experience-${(x.experience_name || "").toLowerCase().replace(/\s+/g, "-")}`}>
      {x.experience_image && <img src={thumbUrl(x.experience_image)} alt={x.experience_name} className="w-full aspect-[16/9] object-cover" />}
      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-display font-bold">{x.experience_name}</h4>
          {x.status && <span className={`text-[10px] px-2 py-0.5 rounded-full ${closed ? "bg-rose-500/20 text-rose-300" : x.status === "Open" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{x.status}</span>}
        </div>
        {(x.experience_type || x.duration) && <p className="text-xs text-zinc-500 mt-0.5">{[x.experience_type, x.duration].filter(Boolean).join(" · ")}</p>}
        {x.description && <p className="text-sm text-zinc-400 mt-2">{x.description}</p>}
        <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-zinc-400">
          {x.schedule_times && <span className="bg-white/5 px-2 py-0.5 rounded-full">{x.schedule_times}</span>}
          {x.min_age && <span className="bg-white/5 px-2 py-0.5 rounded-full">Age {x.min_age}+</span>}
          {x.capacity && <span className="bg-white/5 px-2 py-0.5 rounded-full">Up to {x.capacity}</span>}
          {x.additional_fee && <span className="bg-white/5 px-2 py-0.5 rounded-full">Extra fee ${x.additional_fee}</span>}
          {x.seasonal && <span className="bg-white/5 px-2 py-0.5 rounded-full">{x.seasonal}</span>}
        </div>
      </div>
    </div>
  );
}

function ZoneBlock({ zone, rides, games, experiences }) {
  const count = rides.length + games.length + experiences.length;
  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden bg-white/[0.02]" data-testid={`zone-${(zone.zone_name || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="p-5 pb-0 flex items-start gap-4">
        {zone.zone_image && <img src={thumbUrl(zone.zone_image)} alt={zone.zone_name} className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shrink-0 border border-white/10" />}
        <div className="min-w-0">
          <h3 className="font-display font-bold text-2xl">{zone.zone_name}</h3>
          {zone.description && <p className="text-sm text-zinc-300 mt-1 max-w-2xl">{zone.description}</p>}
          <p className="text-[11px] uppercase tracking-wider text-amber-300 mt-1.5 font-bold">{count} thing{count === 1 ? "" : "s"} to do</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 p-5">
        {rides.map((r, i) => <RideCard key={`${r.ride_name}-${i}`} r={r} />)}
        {games.map((g, i) => <GameCard key={`${g.game_name}-${i}`} g={g} />)}
        {experiences.map((x, i) => <ExperienceCard key={`${x.experience_name}-${i}`} x={x} />)}
      </div>
    </div>
  );
}

function RideCard({ r }) {
  const closed = ["Closed", "Temporarily Closed", "Under Maintenance"].includes(r.status);
  return (
    <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden" data-testid={`ride-${(r.ride_name || "").toLowerCase().replace(/\s+/g, "-")}`}>
      {r.ride_image && <img src={thumbUrl(r.ride_image)} alt={r.ride_name} className="w-full aspect-[16/9] object-cover" />}
      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-display font-bold">{r.ride_name}</h4>
          {r.status && <span className={`text-[10px] px-2 py-0.5 rounded-full ${closed ? "bg-rose-500/20 text-rose-300" : r.status === "Open" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{r.status}</span>}
        </div>
        {(r.ride_type || r.thrill_level || r.zone) && <p className="text-xs text-zinc-500 mt-0.5">{[r.ride_type, r.thrill_level, r.indoor_outdoor, r.zone].filter(Boolean).join(" · ")}</p>}
        {r.description && <p className="text-sm text-zinc-400 mt-2">{r.description}</p>}
        <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-zinc-400">
          {r.min_height && <span className="bg-white/5 px-2 py-0.5 rounded-full">Min height {r.min_height}</span>}
          {r.min_age && <span className="bg-white/5 px-2 py-0.5 rounded-full">Age {r.min_age}+</span>}
          {r.supervision_required && <span className="bg-white/5 px-2 py-0.5 rounded-full">{r.supervision_required}</span>}
          {r.additional_fee && <span className="bg-white/5 px-2 py-0.5 rounded-full">Extra fee ${r.additional_fee}</span>}
        </div>
      </div>
    </div>
  );
}

function GameCard({ g }) {
  return (
    <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
      {g.game_image && <img src={thumbUrl(g.game_image)} alt={g.game_name} className="w-full aspect-[16/9] object-cover" />}
      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-display font-bold">{g.game_name}</h4>
          {g.price && <span className="text-rose-400 font-bold text-sm">${String(g.price).replace(/^\$/, "")}</span>}
        </div>
        {g.game_type && <p className="text-xs text-zinc-500 mt-0.5">{[g.game_type, g.players && `${g.players} players`, g.zone].filter(Boolean).join(" · ")}</p>}
        {g.description && <p className="text-sm text-zinc-400 mt-2">{g.description}</p>}
        {g.prize_info && <p className="text-xs text-amber-300 mt-2">🏆 {g.prize_info}</p>}
      </div>
    </div>
  );
}

export default function AttractionDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/attractions/${slug}`).then((r) => setA(r.data)).catch(() => setErr(true)); window.scrollTo(0, 0); }, [slug]);
  useSeo(a ? (a.seo_title || `${a.name} | Gulf Coast VIP`) : "Attraction", a?.seo_description || a?.short_description);

  if (err) return <div className="py-32 text-center text-zinc-500">Attraction not found. <Link to="/attractions" className="text-rose-400">Browse attractions</Link></div>;
  if (!a) return <div className="py-32 text-center text-zinc-600 animate-pulse">Loading…</div>;

  const tickets = (a.tickets || []).filter((t) => t.status !== "off");
  const zones = (a.zones || []).filter((z) => z.zone_name);
  const zoneNames = new Set(zones.map((z) => z.zone_name));
  const inZone = (list, name) => (list || []).filter((x) => (x.zone || "") === name);
  const unzoned = (list) => (list || []).filter((x) => !x.zone || !zoneNames.has(x.zone));
  const looseRides = unzoned(a.rides), looseGames = unzoned(a.games), looseExps = unzoned(a.experiences);

  return (
    <div data-testid="attraction-detail">
      <section className="relative min-h-[46vh] flex flex-col justify-end overflow-hidden">
        <ListingHeroImg ent={a} kind="attraction" className="absolute inset-0 w-full h-full object-cover hero-zoom" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        <div className="relative px-6 md:px-12 lg:px-20 pb-8 pt-28">
          <Link to="/attractions" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-3"><ArrowLeft size={14} /> All attractions</Link>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {a.attraction_type && <span className="bg-rose-500 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{a.attraction_type}</span>}
            {a.family_friendly && <span className="glass text-[11px] px-2.5 py-1 rounded-full">Family friendly</span>}
            {a.indoor_outdoor && <span className="glass text-[11px] px-2.5 py-1 rounded-full">{a.indoor_outdoor}</span>}
            {a.award_badges?.length > 0 && <AwardBadges badges={a.award_badges} />}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl">{a.name}</h1>
          <p className="text-zinc-300 mt-2 flex items-center gap-1.5"><MapPin size={15} /> {[a.address, a.city, a.state].filter(Boolean).join(", ")}</p>
          {a.business && <p className="text-sm text-zinc-400 mt-1">Operated by <Link to={`/businesses/${a.business.slug}`} className="text-rose-400 hover:underline" data-testid="attraction-business-link">{a.business.name}</Link></p>}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <ListingActions entityType="attraction" entityId={a.id} claimStatus={a.claim_status || "unclaimed"} verified={!!a.verified} />
            <NominateButton entityType="attraction" entity={a} />
            {a.ticket_link && <a href={a.ticket_link} target="_blank" rel="noreferrer" className="bg-rose-500 hover:bg-rose-600 font-bold text-sm px-5 py-2.5 rounded-full flex items-center gap-2" data-testid="attraction-tickets-btn"><Ticket size={15} /> Get Tickets</a>}
          </div>
        </div>
      </section>

      <LoyaltyJoinCard entityType="attraction" entityId={a.id} />

      <div className="px-6 md:px-12 lg:px-20 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          {(a.short_description || a.description) && (
            <Reveal><SectionTitle kicker="About" title="Overview" />
              <PipeText text={a.description || a.short_description} className="text-zinc-300 leading-relaxed" /></Reveal>
          )}

          {tickets.length > 0 && (
            <Reveal className="lg:hidden"><SectionTitle kicker="Admission" title="Tickets & Passes" />
              <div className="space-y-3" data-testid="attraction-tickets">
                {tickets.map((t, i) => (
                  <div key={`${t.ticket_name || t.ticket_type}-${i}`} className="flex flex-wrap items-center justify-between gap-3 bg-[#111] border border-white/10 rounded-xl p-4">
                    <div className="min-w-0">
                      <p className="font-bold">{t.ticket_name || t.ticket_type}</p>
                      <p className="text-xs text-zinc-500">{[t.ticket_type, t.description].filter(Boolean).join(" · ")}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        {t.sale_price ? <><span className="text-zinc-500 line-through text-sm mr-2">${String(t.price).replace(/^\$/, "")}</span><span className="font-display font-bold text-rose-400 text-lg">${String(t.sale_price).replace(/^\$/, "")}</span></>
                          : t.price ? <span className="font-display font-bold text-rose-400 text-lg">${String(t.price).replace(/^\$/, "")}</span> : null}
                      </div>
                      {t.status === "sold_out" ? <span className="text-xs bg-zinc-500/20 text-zinc-400 px-3 py-1.5 rounded-full">Sold out</span>
                        : (t.external_link || a.ticket_link) && <a href={t.external_link || a.ticket_link} target="_blank" rel="noreferrer" className="text-xs font-bold bg-rose-500 hover:bg-rose-600 px-4 py-2 rounded-full">Buy</a>}
                    </div>
                  </div>
                ))}
              </div></Reveal>
          )}

          {zones.length > 0 && (
            <Reveal><SectionTitle kicker="Find your way around" title="Explore by Zone" />
              <div className="space-y-6 mt-6" data-testid="attraction-zones">
                {zones.map((z) => (
                  <ZoneBlock key={z.zone_name} zone={z}
                    rides={inZone(a.rides, z.zone_name)}
                    games={inZone(a.games, z.zone_name)}
                    experiences={inZone(a.experiences, z.zone_name)} />
                ))}
              </div></Reveal>
          )}

          {looseRides.length > 0 && (
            <Reveal><SectionTitle kicker="Thrills" title={zones.length ? "More Rides" : "Rides"} />
              <Paged items={looseRides} perKey="attraction_rides_pp" perDefault={6} label="rides" testid="attraction-rides" render={(slice) => (
                <div className="grid sm:grid-cols-2 gap-4">{slice.map((r, i) => <RideCard key={`${r.ride_name}-${i}`} r={r} />)}</div>
              )} /></Reveal>
          )}

          {looseGames.length > 0 && (
            <Reveal><SectionTitle kicker="Play" title={zones.length ? "More Games" : "Games"} />
              <Paged items={looseGames} perKey="attraction_games_pp" perDefault={6} label="games" testid="attraction-games" render={(slice) => (
                <div className="grid sm:grid-cols-2 gap-4">{slice.map((g, i) => <GameCard key={`${g.game_name}-${i}`} g={g} />)}</div>
              )} /></Reveal>
          )}

          {looseExps.length > 0 && (
            <Reveal><SectionTitle kicker="Beyond the rides" title={zones.length ? "More Experiences" : "Experiences"} />
              <Paged items={looseExps} perKey="attraction_experiences_pp" perDefault={6} label="experiences" testid="attraction-experiences" render={(slice) => (
                <div className="grid sm:grid-cols-2 gap-4">{slice.map((x, i) => <ExperienceCard key={`${x.experience_name}-${i}`} x={x} />)}</div>
              )} /></Reveal>
          )}

          {(a.upcoming_events || []).length > 0 && (
            <Reveal><SectionTitle kicker="Happening here" title="Upcoming Events" />
              <Paged items={a.upcoming_events} perKey="attraction_events_pp" perDefault={6} label="events" testid="attraction-events" render={(slice) => (
                <div className="grid sm:grid-cols-2 gap-4">{slice.map((e) => <EventCard key={e.id} e={e} />)}</div>
              )} /></Reveal>
          )}

          {a.deals?.length > 0 && <Reveal><Paged items={a.deals} perKey="attraction_deals_pp" perDefault={4} label="deals" testid="attraction-deals-paged" render={(slice) => <DealsSection deals={slice} />} /></Reveal>}

          {a.marketplace_merch?.length > 0 && <Reveal><Paged items={a.marketplace_merch} perKey="attraction_merch_pp" perDefault={6} label="items" testid="attraction-merch-paged" render={(slice) => <MerchGrid items={slice} title="Shop & Tickets" />} /></Reveal>}

          {a.business && (
            <Reveal>
              <SectionTitle kicker="Operated by" title="The Business & Directions" />
              <Link to={`/businesses/${a.business.slug}`} className="group block rounded-2xl overflow-hidden bg-[#111] border border-white/10 card-hover mt-6" data-testid="attraction-business-card">
                <div className="relative h-52 overflow-hidden">
                  {a.business.hero_image
                    ? <img src={fileUrl(a.business.hero_image)} alt={a.business.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="w-full h-full bg-gradient-to-br from-rose-500/30 via-black to-black" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  {a.business.logo && <img src={fileUrl(a.business.logo)} alt="" className="absolute top-4 left-4 w-14 h-14 rounded-xl object-cover border border-white/20 bg-black/40 backdrop-blur" />}
                  <div className="absolute bottom-0 p-5">
                    {a.business.business_type && <p className="overline">{a.business.business_type}</p>}
                    <h3 className="font-display font-bold text-2xl">{a.business.name}</h3>
                    {(a.business.address || a.business.city) && (
                      <p className="text-zinc-300 text-sm mt-1 flex items-center gap-1"><MapPin size={13} /> {[a.business.address, a.business.city, a.business.state].filter(Boolean).join(", ")}</p>
                    )}
                    {a.business.short_description && <p className="text-zinc-400 text-sm mt-2 line-clamp-2 max-w-xl">{a.business.short_description}</p>}
                  </div>
                </div>
              </Link>
              <div className="mt-5 rounded-2xl overflow-hidden border border-white/10">
                <iframe title="business-map" src={MAP([a.business.address, a.business.city, a.business.state].filter(Boolean).join(", ") || `${a.city}, ${a.state}`)} className="w-full h-72 grayscale invert-[.9]" loading="lazy" />
              </div>
            </Reveal>
          )}

          {(a.gallery_images || []).length > 0 && (
            <Reveal><SectionTitle kicker="In frame" title="Gallery" />
              <Paged items={a.gallery_images} perKey="attraction_gallery_pp" perDefault={12} label="photos" testid="attraction-gallery" render={(slice) => (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {slice.map((g, i) => <img key={`${typeof g === "string" ? g : g.image_url || g.url}-${i}`} src={fileUrl(typeof g === "string" ? g : g.image_url || g.url)} alt="" className="w-full aspect-square object-cover rounded-xl" loading="lazy" />)}
                </div>
              )} /></Reveal>
          )}

          {a.videos?.length > 0 && <Reveal><Paged items={a.videos} perKey="attraction_videos_pp" perDefault={6} label="videos" testid="attraction-videos" render={(slice) => <VideoGrid items={slice} />} /></Reveal>}

          {(a.safety_info || a.rules) && (
            <Reveal className="lg:hidden"><SectionTitle kicker="Know before you go" title="Rules & Safety" />
              {a.safety_info && <div className="text-zinc-300 mb-4 flex gap-2"><ShieldCheck size={15} className="text-rose-400 shrink-0 mt-1" /><PipeText text={a.safety_info} /></div>}
              {a.rules && <PipeText text={a.rules} className="text-zinc-400 text-sm" />}</Reveal>
          )}

          {a.sponsors?.length > 0 && <Reveal><Sponsors items={a.sponsors} /></Reveal>}
          <ReviewsSection entityType="attraction" entityId={a.id} ownerUserId={a.owner_user_id} />
        </div>

        <aside className="space-y-6">
          {/* VIP Spotlight (persistent across all detail pages) */}
          <div className="hidden lg:block"><VoteCard entityType="attraction" entity={a} compact /></div>

          {/* 1. Weather now */}
          <WeatherPanel city={a.city} state={a.state} weatherPolicy={a.weather_policy} />

          {/* 2. Tickets & Passes */}
          {tickets.length > 0 && (
            <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="attraction-side-tickets">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-display font-bold text-lg flex items-center gap-2"><Ticket size={16} className="text-rose-400" /> Tickets & Passes</h3>
                {a.ticket_link && <a href={a.ticket_link} target="_blank" rel="noreferrer" className="text-xs font-bold text-rose-400 hover:text-rose-300" data-testid="attraction-side-tickets-all">All Tickets</a>}
              </div>
              <Paged items={tickets} perKey="attraction_side_tickets_pp" perDefault={3} label="tickets" testid="attraction-side-tickets-paged" render={(slice) => (
                <ul className="space-y-2.5">
                  {slice.map((t, i) => (
                    <li key={`${t.ticket_name || t.ticket_type}-${i}`} className="bg-white/[0.03] border border-white/10 rounded-xl p-3" data-testid={`attraction-side-ticket-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm text-white leading-tight">{t.ticket_name || t.ticket_type}</p>
                          {t.ticket_type && t.ticket_name && <p className="text-[11px] text-zinc-500 mt-0.5">{t.ticket_type}</p>}
                          {t.description && <p className="text-xs text-zinc-400 mt-1.5 line-clamp-2">{t.description}</p>}
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <div className="text-right">
                            {t.sale_price ? <><p className="text-[10px] text-zinc-500 line-through leading-none">${String(t.price).replace(/^\$/, "")}</p><p className="font-display font-bold text-rose-400 text-base leading-tight">${String(t.sale_price).replace(/^\$/, "")}</p></>
                              : t.price ? <p className="font-display font-bold text-rose-400 text-base">${String(t.price).replace(/^\$/, "")}</p> : null}
                          </div>
                          {t.status === "sold_out"
                            ? <span className="text-[10px] bg-zinc-500/20 text-zinc-400 px-2 py-1 rounded-full">Sold out</span>
                            : (t.external_link || a.ticket_link) && <a href={t.external_link || a.ticket_link} target="_blank" rel="noreferrer" className="text-[11px] font-bold bg-rose-500 hover:bg-rose-600 text-white px-3 py-1.5 rounded-full" data-testid={`attraction-side-ticket-buy-${i}`}>Buy</a>}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )} />
            </div>
          )}

          {/* 3. Visitor Info */}
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 space-y-5" data-testid="attraction-info-panel">
            <h3 className="font-display font-bold text-lg">Visitor Info</h3>
            {a.opening_hours && <Info Icon={Clock} label="Hours"><PipeText text={a.opening_hours} /></Info>}
            {a.seasonal_hours && <Info Icon={Clock} label="Seasonal hours"><PipeText text={a.seasonal_hours} /></Info>}
            {a.admission_price && <Info Icon={Ticket} label="Admission">{String(a.admission_price).startsWith("$") ? a.admission_price : `$${a.admission_price}`}</Info>}
            {a.age_recommendations && <Info Icon={ShieldCheck} label="Ages">{a.age_recommendations}</Info>}
            {a.parking_info && <Info Icon={Car} label="Parking">{a.parking_info}</Info>}
            {a.accessibility_info && <Info Icon={Accessibility} label="Accessibility">{a.accessibility_info}</Info>}
            {a.phone && <Info Icon={Phone} label="Phone"><a href={`tel:${a.phone}`} className="hover:text-rose-400">{a.phone}</a></Info>}
            {a.email && <Info Icon={Mail} label="Email"><a href={`mailto:${a.email}`} className="hover:text-rose-400">{a.email}</a></Info>}
            {a.website && <Info Icon={Globe} label="Website"><a href={a.website} target="_blank" rel="noreferrer" className="hover:text-rose-400 break-all">{a.website}</a></Info>}
          </div>

          {/* 4. Amenities */}
          {(a.amenities || []).length > 0 && (
            <div className="bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="attraction-side-amenities">
              <h3 className="font-display font-bold text-lg mb-3">Amenities</h3>
              <Paged items={a.amenities} perKey="attraction_side_amenities_pp" perDefault={12} label="amenities" testid="attraction-side-amenities-paged" render={(slice) => (
                <div className="flex flex-wrap gap-2">{slice.map((am) => <span key={am} className="text-xs bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">{am}</span>)}</div>
              )} />
            </div>
          )}

          {/* 5. Food & Dining */}
          {a.food_dining && (
            <div className="bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="attraction-side-food">
              <h3 className="font-display font-bold text-lg mb-2">Food & Dining</h3>
              <PipeText text={a.food_dining} className="text-sm text-zinc-300" />
            </div>
          )}

          {/* 6. Rules & Safety */}
          {(a.safety_info || a.rules) && (
            <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="attraction-side-rules">
              <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><ShieldCheck size={16} className="text-rose-400" /> Rules & Safety</h3>
              {a.safety_info && <PipeText text={a.safety_info} className="text-sm text-zinc-300 leading-relaxed" />}
              {a.rules && <PipeText text={a.rules} className="text-xs text-zinc-400 mt-3 leading-relaxed" />}
            </div>
          )}

          {/* 7. Map & Directions */}
          <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="attraction-side-map">
            <h3 className="font-display font-bold text-lg mb-4">Map & Directions</h3>
            <div className="rounded-xl overflow-hidden border border-white/10">
              <iframe title="map" src={MAP([a.address, a.city, a.state].filter(Boolean).join(", ") || `${a.city}, ${a.state}`)} className="w-full h-56 grayscale invert-[.9]" loading="lazy" />
            </div>
            <a href={a.map_link || `https://maps.google.com/?q=${encodeURIComponent([a.address, a.city, a.state].filter(Boolean).join(", "))}`}
              target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-rose-300 hover:text-rose-200" data-testid="attraction-directions"><Navigation size={12} /> Get directions</a>
          </div>
        </aside>
      </div>
    </div>
  );
}
